import axios from '../config/axios';
import API_ENDPOINTS from '../config/api';

const generateScheduleOfActivities = async (data) => {
  try {
    // Ensure protocolInput is properly formatted
    const formattedData = {
      ...data,
      protocolInput: data.protocolInput || 'Generate a standard Schedule of Activities table for a clinical trial',
      promptHistory: data.promptHistory || []
    };

    const response = await axios.post(API_ENDPOINTS.scheduleOfActivities.generate, formattedData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

const saveScheduleOfActivities = async (data) => {
  try {
    // Include prompt information in the saved data
    const saveData = {
      ...data,
      metadata: {
        ...data.metadata,
        originalPrompt: data.originalPrompt || 'Generate a standard Schedule of Activities table for a clinical trial',
        currentPrompt: data.currentPrompt || '',
        lastUpdated: new Date().toISOString()
      }
    };

    const response = await axios.post(API_ENDPOINTS.scheduleOfActivities.save, saveData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

const getScheduleOfActivities = async (protocolId) => {
  try {
    const response = await axios.get(API_ENDPOINTS.scheduleOfActivities.get(protocolId));
    return response.data;
  } catch (error) {
    throw error;
  }
};

const updateScheduleOfActivities = async (protocolId, data) => {
  try {
    const response = await axios.put(API_ENDPOINTS.scheduleOfActivities.update(protocolId), data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

const exportSoAToExcel = async (protocolId) => {
  try {
    const response = await axios.get(API_ENDPOINTS.scheduleOfActivities.exportExcel(protocolId), {
      responseType: 'blob'
    });
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `SOA_${protocolId}_${new Date().toISOString().split('T')[0]}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return { success: true };
  } catch (error) {
    throw error;
  }
};

const importSoAFromExcel = async (protocolId, file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await axios.post(API_ENDPOINTS.scheduleOfActivities.importExcel(protocolId), formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Get version history for a protocol
const getVersionHistory = async (protocolId) => {
  try {
    const response = await axios.get(`${API_ENDPOINTS.scheduleOfActivities.get(protocolId)}/versions`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Get specific version by version number
const getVersionByNumber = async (protocolId, versionNumber) => {
  try {
    const response = await axios.get(`${API_ENDPOINTS.scheduleOfActivities.get(protocolId)}/versions/${versionNumber}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Rollback to a specific version
const rollbackToVersion = async (protocolId, versionNumber) => {
  try {
    const response = await axios.post(`${API_ENDPOINTS.scheduleOfActivities.get(protocolId)}/rollback/${versionNumber}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Export to M11-compliant XML
const exportToM11XML = async (protocolId, versionNumber = null) => {
  try {
    const url = versionNumber 
      ? `${API_ENDPOINTS.scheduleOfActivities.get(protocolId)}/export-m11-xml/${versionNumber}`
      : `${API_ENDPOINTS.scheduleOfActivities.get(protocolId)}/export-m11-xml`;
    
    const response = await axios.get(url, {
      responseType: 'text' // XML is returned as text
    });
    
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Get M11 metadata
const getM11Metadata = async (protocolId) => {
  try {
    const response = await axios.get(`${API_ENDPOINTS.scheduleOfActivities.get(protocolId)}/m11-metadata`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Validate M11 XML
const validateM11XML = async (protocolId) => {
  try {
    const response = await axios.get(`${API_ENDPOINTS.scheduleOfActivities.get(protocolId)}/validate-m11-xml`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Export to Excel (updated to return blob data)
const exportToExcel = async (protocolId) => {
  try {
    const response = await axios.get(API_ENDPOINTS.scheduleOfActivities.exportExcel(protocolId), {
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

const scheduleOfActivitiesService = {
  generateScheduleOfActivities,
  saveScheduleOfActivities,
  getScheduleOfActivities,
  updateScheduleOfActivities,
  exportSoAToExcel,
  importSoAFromExcel,
  getVersionHistory,
  getVersionByNumber,
  rollbackToVersion,
  exportToM11XML,
  getM11Metadata,
  validateM11XML,
  exportToExcel
};

export default scheduleOfActivitiesService;

